import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PAGE_SIZE } from "../configs/constants/Pagination";
import { ApiErrorResponse } from "../models/ApiResponse";
import {
	disable2FARequest,
	enable2FARequest,
	InviteOption,
	sendCodeRequest,
	UpdateUserRequest,
	User,
	verify2FARequest,
} from "../models/InternalUser";
import { apiStore } from "../stores/api.store";

/**
 * Internal users double as consultant managers, which the manager dropdowns
 * cache separately (useConsultantManagers/useConsultantManager, 60 s
 * staleTime) — refresh those too whenever an internal user changes.
 */
const invalidateConsultantManagerQueries = (queryClient: QueryClient) => {
	queryClient.invalidateQueries(["organisation", "consultant-managers"]);
	queryClient.invalidateQueries(["organisation", "consultant-manager"]);
};

export type GetInternalUsersParams = {
	page?: number;
	keyword?: string;
	/** Backend projection, e.g. "select_option" for slim dropdown data. */
	view?: string;
};

/**
 * Internal (organisation-account) users: admins and consultant managers.
 *
 * `page` defaults to the first one: the backend turns it into `start` and
 * answers 400 ("data.start should be number") for a missing or non-numeric
 * value, so a caller that only cares about the first page must not have to
 * remember it.
 */
export function useInternalUsers(
	query: GetInternalUsersParams = {},
	options: { enabled?: boolean } = {}
) {
	const { data, ...rest } = useQuery(
		["internal-users", query],
		() =>
			apiStore
				.get<{ users: User[]; totalCount: number }>({
					path: `/organisation-account?page=${query.page ?? 0}&pageSize=${PAGE_SIZE}${
						query.keyword ? `&keyword=${query.keyword}` : ""
					}${query.view ? `&view=${query.view}` : ""}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{ keepPreviousData: true, enabled: options.enabled ?? true }
	);

	return {
		users: data?.users ?? [],
		totalUserCount: data?.totalCount ?? 0,
		...rest,
	};
}

/**
 * A single internal user, mapped to the user form's view model — local phone
 * number without country code, empty password field, etc. (was
 * UsersStore.getInternalUser).
 */
export function useInternalUser(userId?: string) {
	const { data, ...rest } = useQuery(
		["internal-users", "detail", userId],
		() =>
			apiStore
				.get({
					path: `/organisation-account/${userId}`,
					isAuthRequired: true,
				})
				.then(({ data }): User => {
					return {
						active: data.active,
						email: data.email,
						firstName: data.firstName,
						lastName: data.lastName,
						password: "",
						roles: data.roles,
						phone:
							data.profile?.phone
								?.toString()
								.substring(3, data.profile?.phone.toString().length + 1) || "",
						countryCode: "+46",
						twoFactor: data?.twoFactor || undefined,
					};
				}),
		{ enabled: !!userId }
	);

	return {
		user: data,
		...rest,
	};
}

export function useUpdateInternalUser() {
	const queryClient = useQueryClient();

	return useMutation<unknown, ApiErrorResponse, { userId: string; update: UpdateUserRequest }>(
		(params) =>
			apiStore
				.put({
					path: `/organisation-account/${params.userId}`,
					body: params.update,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["internal-users"]);
				invalidateConsultantManagerQueries(queryClient);
			},
		}
	);
}

export function useCreateInternalUser() {
	const queryClient = useQueryClient();

	return useMutation<User & { id?: string }, ApiErrorResponse, UpdateUserRequest>(
		(body) =>
			apiStore
				.post({
					path: `/organisation-account`,
					body,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["internal-users"]);
				invalidateConsultantManagerQueries(queryClient);
			},
		}
	);
}

export function useDeleteInternalUser() {
	const queryClient = useQueryClient();

	return useMutation<unknown, ApiErrorResponse, string>(
		(userId) =>
			apiStore
				.delete({
					path: `/user/${userId}`,
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				queryClient.invalidateQueries(["internal-users"]);
				invalidateConsultantManagerQueries(queryClient);
			},
		}
	);
}

/** Send an app/dashboard invitation to any user (consultant, customer user, internal). */
export function useInviteUser() {
	return useMutation<unknown, ApiErrorResponse, { userId: string; channels: InviteOption }>(
		(params) =>
			apiStore
				.post({
					path: `/invitation/${params.userId}/invite`,
					body: { channels: params.channels },
					isAuthRequired: true,
				})
				.then((d) => d.data)
	);
}

export function useEnable2FA() {
	return useMutation<{ verificationId?: string; ttl?: number }, ApiErrorResponse, enable2FARequest>(
		(body) =>
			apiStore
				.post({
					path: `/auth/enable-tfa/sms`,
					body,
					isAuthRequired: true,
				})
				.then((d) => d.data)
	);
}

export function useDisable2FA() {
	return useMutation<unknown, ApiErrorResponse, disable2FARequest>((body) =>
		apiStore
			.post({
				path: `/auth/disable-tfa/sms`,
				body,
				isAuthRequired: true,
			})
			.then((d) => d.data)
	);
}

export function useVerify2FA() {
	return useMutation<unknown, ApiErrorResponse, verify2FARequest>((body) =>
		apiStore
			.post({
				path: `/auth/verify-tfa/sms`,
				body,
				isAuthRequired: true,
			})
			.then((d) => d.data)
	);
}

export function useSendTwoFactorCode() {
	return useMutation<{ verificationId?: string; ttl?: string }, ApiErrorResponse, sendCodeRequest>(
		(body) =>
			apiStore
				.post({
					path: `/auth/sms/send-code`,
					body,
					isAuthRequired: true,
				})
				.then((d) => d.data)
	);
}

/** Consultant managers of an org (slim select view) for e.g. transfer targets. */
export function useConsultantManagersByOrg(organisationId?: string) {
	const { data, ...rest } = useQuery(
		["internal-users", "consultant-managers", organisationId],
		() =>
			apiStore
				.get<{ users: User[] }>({
					path: `/organisation-account?pageSize=1000&view=select&roles=organisation_consultant_manager&organisationId=${organisationId}`,
					isAuthRequired: true,
				})
				.then((d) => d.data.users || []),
		{ enabled: !!organisationId }
	);

	return {
		managers: data ?? [],
		...rest,
	};
}

/** Move every consultant of one manager to another. */
export function useTransferConsultants() {
	const queryClient = useQueryClient();

	return useMutation<
		{ totalConsultants: number; successCount: number; failedCount: number },
		ApiErrorResponse,
		{ sourceManagerId: string; targetManagerId: string }
	>(
		(params) =>
			apiStore
				.post({
					path: `/organisation-account/${params.sourceManagerId}/transfer-consultants`,
					body: { targetManagerId: params.targetManagerId },
					isAuthRequired: true,
				})
				.then((d) => d.data),
		{
			onSuccess: () => {
				// Consultant profiles carry their manager, so refresh those too
				queryClient.invalidateQueries(["consultants"]);
			},
		}
	);
}

/** Number of consultants assigned to a manager (used before transfer). */
export function useConsultantCountByManager() {
	return useMutation<number, ApiErrorResponse, string>((managerId) =>
		apiStore
			.get<{ totalCount: number }>({
				path: `/consultant-account?managerId=${managerId}`,
				isAuthRequired: true,
			})
			.then((d) => d.data.totalCount || 0)
	);
}
